import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useMobileChrome } from "../stores/mobileChrome";
import { Spinner } from "./ui";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export function AiChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: status } = useQuery({
    queryKey: ["ai-status"],
    queryFn: () => api<{ feReady: boolean }>("/ai/status"),
    staleTime: 300_000,
  });
  const bottomNavHidden = useMobileChrome((s) => s.bottomNavSuppressed > 0);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  if (!status?.feReady) return null;

  async function send() {
    const message = input.trim();
    if (!message || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    try {
      const res = await api<{ conversationId: string; reply: string }>("/ai/chat/fe", {
        body: { conversationId, message },
      });
      setConversationId(res.conversationId);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: err instanceof Error ? err.message : "Something went wrong." },
      ]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`fixed right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-brand text-xl shadow-xl shadow-brand-pink/30 active:scale-95 lg:bottom-4 ${
          bottomNavHidden
            ? "bottom-4"
            : "bottom-[calc(var(--mobile-nav-height)+0.75rem+env(safe-area-inset-bottom))]"
        }`}
        aria-label="Support chat"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>

      {open && (
        <div
          className={`fixed right-4 z-50 flex h-[28rem] w-80 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-surface shadow-2xl lg:bottom-20 ${
            bottomNavHidden
              ? "bottom-20"
              : "bottom-[calc(var(--mobile-nav-height)+2rem+env(safe-area-inset-bottom))]"
          }`}
        >
          <div className="border-b border-white/10 bg-gradient-brand px-4 py-3">
            <p className="font-display text-xs font-bold text-white">Gaole Assistant</p>
            <p className="text-[10px] text-white/80">Orders, refunds, tracking & more</p>
          </div>
          <div className="flex-1 space-y-2.5 overflow-y-auto p-3">
            {messages.length === 0 && (
              <div className="space-y-2 py-4 text-center">
                <p className="text-xs text-white/60">Hi! Ask me anything, e.g.:</p>
                {["Where is my order?", "I want a refund", "What packs are available?"].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setInput(suggestion)}
                    className="mx-auto block rounded-full border border-brand-pink/30 px-3 py-1 text-[11px] text-brand-pink"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-xs leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-brand-blue/40 text-white"
                    : "bg-white/5 text-white/90"
                }`}
              >
                {m.content}
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 px-2 py-1">
                <Spinner className="h-3.5 w-3.5" />
                <span className="text-[11px] text-white/40">Thinking…</span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="flex gap-2 border-t border-white/10 p-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type a message…"
              className="flex-1 rounded-xl bg-white/5 px-3 py-2 text-xs text-white outline-none placeholder:text-white/30"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              className="flex items-center justify-center rounded-xl bg-gradient-brand px-3 disabled:opacity-40"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
